// lib
import { useMemo } from 'react'
import PropTypes from 'prop-types'

// components
import { Button } from 'primereact/button'
import { Dropdown } from 'primereact/dropdown'

import { SUSPICION_LEVELS } from '@aytm/app/stats/page/data-centrifuge/lib/vectors'
import {
  deriveFilterOptions,
  deriveSuspicionLevelOptions,
  deriveJobControls
} from '@aytm/app/stats/page/data-centrifuge/lib/derive-controls'
import { calcUidFilter, PINNED_STATUS_CLAUSE } from '@aytm/app/stats/lib/query/uid-filter'
import { canFilterResponses } from '@aytm/app/stats/page/data-quality-report/lib/tiers'

// styles
import style from '@aytm/app/stats/page/data-centrifuge/styles/Controls.module.scss'

const Controls = ({ tier, job, model, features, onFetch, onChange, onFilter }) => {
  const {
    filter,
    suspicionLevel,
    scores,
    suspicionScores,
    vectors,
    disabledVectors,
    quarantineUIDs,
    scoresFiltered,
    quarantineUIDsFiltered,
    dcQuarantineEnabled
  } = model

  // The toggle applies a stats-page filter, which keeps the report open beside Filters only where the
  // section follows that filter.
  const filterEnabled = canFilterResponses(tier, features)

  // The run job reaches no handler unless this feature resolves on, so a viewer without it is
  // offered no control at all — clicking one would discard the report and silently restore it.
  const runEnabled = !features.stat_data_cleaning_run?.disabled

  // Filter dropdown options — suppressed (null) when auto-quarantine is off (per legacy block.coffee).
  const filterOptions = useMemo(() => {
    if (!dcQuarantineEnabled) return null
    return deriveFilterOptions({
      scores,
      suspicionScores,
      suspicionLevel,
      vectors,
      disabledVectors,
      quarantineUIDs
    })
  }, [dcQuarantineEnabled, scores, suspicionScores, suspicionLevel, vectors, disabledVectors, quarantineUIDs])

  // Suspicion-level dropdown options — always present.
  const suspicionLevelOptions = useMemo(
    () =>
      deriveSuspicionLevelOptions({
        scores,
        suspicionScores,
        suspicionLevels: SUSPICION_LEVELS,
        vectors,
        disabledVectors,
        quarantineUIDs,
        filter
      }),
    [scores, suspicionScores, vectors, disabledVectors, quarantineUIDs, filter]
  )

  // Run button descriptor (`job` key). The filter toggle reads `filterEnabled` above.
  const jobControls = useMemo(() => deriveJobControls({ job, model }), [job, model])

  // Render as JSX instead of constructing HTML strings so the templates stop
  // being a latent XSS surface if `option.tag` / `option.text` ever come from
  // a non-static source. `option.tag` is today always 'b' or 'span' — allowlist
  // with a safe default of 'span'.
  const itemTemplate = option => {
    const Tag = option.tag === 'b' ? 'b' : 'span'
    return (
      <span className={style['dropdown-label']}>
        <Tag className={style['dropdown-count']}>{option.l}</Tag>
        <Tag className={style['dropdown-label']}>{option.text}</Tag>
      </span>
    )
  }
  const valueTemplate = (option, _props) => <span>{option ? option.text : _props.placeholder || 'Select...'}</span>

  const onHandleFilter = () => {
    // `scoresFiltered` is the full flagged-response set; the .map runs once per
    // user click (not per render), so memoization would shift work to every
    // render for zero benefit. Leave as-is.
    const uids = scoresFiltered.map(r => r[0])
    if (!uids.length) return

    // The uids already fix the set, so widening the status only keeps a listed respondent in: without it the
    // stats page falls back to completed responses and drops a listed excessive or quarantined one.
    onFilter('uid.*?', calcUidFilter(uids, PINNED_STATUS_CLAUSE))
  }

  return (
    <div data-testid="stat-dc-controls" className={style.component}>
      <div data-testid="stat-dc-controls-meta" className={style.meta}>
        {filterOptions ? (
          <span>
            <b data-testid="stat-dc-controls-quarantined-count">{quarantineUIDsFiltered.length} quarantined</b>
            <br />
            <span data-testid="stat-dc-controls-flagged-count">{scoresFiltered.length} flagged</span>
          </span>
        ) : (
          <span data-testid="stat-dc-controls-flagged-count">{scoresFiltered.length} flagged</span>
        )}
      </div>
      <div className={`${style.controls} p-col p-right`}>
        {suspicionLevelOptions ? (
          <>
            {filterOptions ? (
              <div className="p-fluid p-col-dropdown">
                <Dropdown
                  data-testid="stat-dc-controls-filter"
                  className="p-dropdown-rounded"
                  value={filter || 'all'}
                  options={filterOptions}
                  optionValue="key"
                  optionLabel="text"
                  itemTemplate={itemTemplate}
                  valueTemplate={valueTemplate}
                  fixedDropdownDir="bottom"
                  onChange={event => onChange('filter', event.value)}
                />
              </div>
            ) : null}
            <div className="p-fluid p-col-dropdown">
              <Dropdown
                data-testid="stat-dc-controls-suspicion-level"
                className="p-dropdown-rounded"
                value={suspicionLevel}
                options={suspicionLevelOptions}
                optionValue="key"
                optionLabel="text"
                itemTemplate={itemTemplate}
                valueTemplate={valueTemplate}
                fixedDropdownDir="bottom"
                onChange={event => onChange('suspicionLevel', event.value)}
              />
            </div>
          </>
        ) : null}
        {runEnabled ? (
          <div className="p-fluid p-col-2" style={{ position: 'relative' }}>
            <Button
              data-testid="stat-dc-controls-run"
              className="p-button-rounded p-button-blue"
              label={0 < jobControls.dcAnalyzedCount ? 'Rerun' : 'Run'}
              disabled={jobControls.disabled}
              onClick={jobControls.disabled ? null : onFetch}
            />
            {0 < jobControls.message.length ? (
              <span data-testid="stat-dc-controls-note" className={style.note}>
                {jobControls.message}
              </span>
            ) : 0 < jobControls.notAnalyzedCount ? (
              <span data-testid="stat-dc-controls-note" className={style.note}>
                {jobControls.notAnalyzedCount} not analyzed
              </span>
            ) : null}
          </div>
        ) : null}
        {filterEnabled ? (
          <div className={style.toggle}>
            <i
              data-testid="stat-dc-controls-filter-toggle"
              className="icon-aytmfd-filter-v2"
              onClick={onHandleFilter}
            />
          </div>
        ) : null}
      </div>
    </div>
  )
}

Controls.propTypes = {
  tier: PropTypes.string,
  job: PropTypes.object,
  model: PropTypes.object,
  features: PropTypes.object,
  onFetch: PropTypes.func,
  onChange: PropTypes.func,
  onFilter: PropTypes.func
}

Controls.displayName = 'Controls'

export default Controls
